import React from 'react';
import { Lock, Crown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

/**
 * FeatureGate - Professional locked state overlay for premium features
 * 
 * @param {string} requiredPlan - 'growth' or 'pro'
 * @param {string} currentPlan - User's current plan ('free', 'growth', 'pro')
 * @param {string} featureName - Name of the feature being gated
 * @param {React.ReactNode} children - The feature content to render if unlocked
 * @param {string} variant - 'overlay' (default), 'inline', or 'disabled'
 */
export const FeatureGate = ({ 
  requiredPlan, 
  currentPlan = 'free', 
  featureName,
  children, 
  variant = 'overlay',
  className = ''
}) => {
  const navigate = useNavigate();
  
  const planHierarchy = { free: 0, growth: 1, pro: 2 };
  const isUnlocked = planHierarchy[currentPlan] >= planHierarchy[requiredPlan];
  
  if (isUnlocked) {
    return <>{children}</>;
  }
  
  const planLabels = {
    growth: 'Growth',
    pro: 'Pro'
  };
  
  const handleUpgrade = () => {
    navigate('/pricing');
  };

  // Inline variant - shows a compact locked badge
  if (variant === 'inline') {
    return (
      <div className={cn("relative", className)}>
        <div className="opacity-40 pointer-events-none select-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={handleUpgrade}
            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
          >
            <Lock className="w-3 h-3" />
            <span>{planLabels[requiredPlan]} Plan</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  // Disabled variant - dims the content and shows upgrade prompt on hover
  if (variant === 'disabled') {
    return (
      <div className={cn("relative group", className)}>
        <div className="opacity-50 pointer-events-none select-none filter grayscale">
          {children}
        </div>
        <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
          <button
            onClick={handleUpgrade}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            <Crown className="w-4 h-4" />
            <span>Upgrade to {planLabels[requiredPlan]}</span>
          </button>
        </div>
      </div>
    );
  }

  // Default overlay variant - professional locked card overlay
  return (
    <div className={cn("relative", className)}>
      <div className="opacity-30 pointer-events-none select-none blur-[1px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/60 via-white/80 to-white/60 backdrop-blur-[2px] rounded-lg">
        <div className="text-center px-6 py-5 max-w-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 mb-4">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <h4 className="font-semibold text-slate-800 mb-1 text-base">
            {featureName || 'Premium Feature'}
          </h4>
          <p className="text-sm text-slate-500 mb-4">
            Unlock {featureName?.toLowerCase() || 'this feature'} with the {planLabels[requiredPlan]} plan
          </p>
          <Button
            onClick={handleUpgrade}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            data-testid="upgrade-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * FeatureLockedBadge - Small badge indicator for locked features
 */
export const FeatureLockedBadge = ({ requiredPlan, className = '' }) => {
  const navigate = useNavigate();
  
  const planLabels = {
    growth: 'Growth',
    pro: 'Pro'
  };

  return (
    <button
      onClick={() => navigate('/pricing')}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
        "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200",
        "hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 transition-all duration-200",
        "cursor-pointer",
        className
      )}
      data-testid={`locked-badge-${requiredPlan}`}
    >
      <Lock className="w-3 h-3" />
      <span>{planLabels[requiredPlan]} Plan</span>
    </button>
  );
};

/**
 * Check if user has access to a feature
 */
export const hasFeatureAccess = (currentPlan, requiredPlan) => {
  const planHierarchy = { free: 0, growth: 1, pro: 2 };
  return planHierarchy[currentPlan || 'free'] >= planHierarchy[requiredPlan];
};

export default FeatureGate;
